import type { IconName } from '../../shared/icons.ts';
import { $, restartAnimation } from '../common/dom.ts';
import { icon } from '../common/icons.ts';
import type { SoundBoard } from './sound.ts';

/** Ce que le joueur peut faire en appuyant sur la touche d'action. */
export interface PromptView {
  /** Identifie la cible (évite de réanimer l'invite tant qu'elle ne change pas). */
  key: string;
  icon: IconName;
  label: string;
}

/**
 * Invite « Espace · Soigner vos monstres » affichée quand le joueur fait face à un objet ou un PNJ. Sur écran
 * tactile (`persistent`), c'est le bouton A, toujours affiché : il s'allume quand une action est possible.
 */
export class InteractionPrompt {
  private readonly button = $<HTMLButtonElement>('#interact');
  private readonly flash = $('#heal-flash');
  private readonly sounds: SoundBoard;
  private readonly persistent: boolean;
  private current: PromptView | null = null;

  constructor(onInteract: () => void, sounds: SoundBoard, { persistent = false } = {}) {
    this.sounds = sounds;
    this.persistent = persistent;
    this.button.hidden = !persistent;
    this.button.addEventListener('click', () => onInteract());
  }

  get available(): boolean {
    return this.current !== null;
  }

  /** La cible de l'invite affichée (null : aucune). */
  get key(): string | null {
    return this.current?.key ?? null;
  }

  show(prompt: PromptView | null): void {
    if (prompt?.key === this.current?.key) return;
    this.current = prompt;
    this.button.hidden = !prompt && !this.persistent;
    this.button.classList.toggle('ready', Boolean(prompt));
    this.button.setAttribute('aria-label', prompt ? prompt.label : 'Action (rien à faire ici)');
    $('.interact-icon', this.button).replaceChildren(...(prompt ? [icon(prompt.icon)] : []));
    $('.interact-label', this.button).textContent = prompt?.label ?? '';
    if (prompt) restartAnimation(this.button, 'pop');
  }

  /** Retour visuel et sonore d'un soin à la borne. */
  playHeal(): void {
    this.sounds.play('heal');
    this.flash.hidden = false;
    restartAnimation(this.flash, 'flashing');
    setTimeout(() => { this.flash.hidden = true; }, 1200);
  }
}
